import { useEffect } from "react";
import { useCrewBadges } from "@/hooks/useCrewBadges";
import { onNetworkChange } from "@/offline/network";
import { prefetchOfflinePages } from "@/offline/offlinePages";
import { BottomNav } from "./BottomNav";
import { SideNav } from "./SideNav";
import { SyncStatusBanner } from "./SyncStatusBanner";

export function AppShell({ children }: { children: React.ReactNode }) {
  const { pendingRequests, hasUnreadComments } = useCrewBadges();

  // Only signed-in users see the shell, and only they can log offline: load
  // those pages' code now, or as soon as the connection comes back.
  useEffect(() => {
    // Wait until the browser is idle so the current page loads first.
    const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 2000));
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout;
    const handle = idle(() => void prefetchOfflinePages());
    const stopListening = onNetworkChange((online) => {
      if (online) void prefetchOfflinePages();
    });
    return () => {
      cancelIdle(handle);
      stopListening();
    };
  }, []);

  return (
    <div className="min-h-dvh flex flex-col md:flex-row">
      <SideNav pendingRequests={pendingRequests} hasUnreadComments={hasUnreadComments} />

      {/* Mobile-only top header */}
      <header className="md:hidden flex items-center gap-2 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] border-b border-white/10 bg-bg-base">
        <img src="/logo.png" alt="Sendstats" className="w-7 h-7" />
        <span className="font-display text-lg tracking-wider leading-none">
          <span className="text-accent">SEND</span>
          <span className="text-text-base">STATS</span>
        </span>
      </header>

      {/* No footer here deliberately — legal/support links don't belong on every
          screen of the app shell the way they do on public marketing pages; see
          docs/mobile-app.md. */}
      {/* pb accounts for BottomNav's own height plus its safe-area-inset-bottom padding. */}
      <main className="flex-1 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-8 px-4 pt-6 max-w-lg mx-auto md:max-w-3xl w-full">
        <SyncStatusBanner />
        {children}
      </main>
      <BottomNav pendingRequests={pendingRequests} hasUnreadComments={hasUnreadComments} />
    </div>
  );
}
